import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api';

const CATEGORY_LABELS = {
  plumbing: 'Plumbing',
  roof:     'Roof',
  hvac:     'HVAC',
  panel:    'Electric Panel',
  yard:     'Yard / Exterior',
};

function getCategoryLabel(category) {
  if (category.startsWith('room_')) return 'Rooms';
  return CATEGORY_LABELS[category] || category;
}

function getCategoryColor(category) {
  if (category.startsWith('room_')) return '#2493b5';
  const colors = { plumbing: '#2980b9', roof: '#7f8c8d', hvac: '#16a085', panel: '#e67e22', yard: '#39b54a' };
  return colors[category] || '#939598';
}

function getCategoryIcon(category) {
  if (category.startsWith('room_')) return '🏠';
  const icons = { plumbing: '🚿', roof: '🏗️', hvac: '❄️', panel: '⚡', yard: '🌿' };
  return icons[category] || '📷';
}

// ── Single photo card ─────────────────────────────────────────────────────────

function PhotoCard({ photo, dealId, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [notes,     setNotes]     = useState(photo.notes || '');
  const inputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('category', photo.category);
      const res = await fetch(`/api/deals/${dealId}/photos/${photo.id}/upload`, {
        method: 'POST', body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      onRefresh();
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove this photo?')) return;
    await api.deletePhotoImage(dealId, photo.id);
    onRefresh();
  };

  const handleNotesBlur = async () => {
    if (notes !== (photo.notes || '')) {
      await api.updatePhotoNotes(dealId, photo.id, notes);
    }
  };

  const done = !!photo.url;

  return (
    <div style={{
      border: `1px solid ${done ? '#c3e6cb' : 'var(--border)'}`,
      borderRadius: 8, overflow: 'hidden',
      background: done ? '#f0fff4' : 'var(--card)',
      transition: 'border-color 0.2s',
    }}>
      {/* Photo or placeholder */}
      <div
        style={{
          height: 130, background: done ? '#000' : 'var(--bg)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', position: 'relative', overflow: 'hidden',
        }}
        onClick={() => !done && inputRef.current?.click()}
      >
        {done ? (
          <>
            <img src={photo.url} alt={photo.label}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <div style={{
              position: 'absolute', top: 4, right: 4, display: 'flex', gap: 4,
            }}>
              <button
                onClick={e => { e.stopPropagation(); inputRef.current?.click(); }}
                style={{ background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none',
                  borderRadius: 4, padding: '3px 7px', fontSize: 11, cursor: 'pointer' }}
              >Replace</button>
              <button
                onClick={e => { e.stopPropagation(); handleDelete(); }}
                style={{ background: 'rgba(214,48,49,0.8)', color: '#fff', border: 'none',
                  borderRadius: 4, padding: '3px 7px', fontSize: 11, cursor: 'pointer' }}
              >✕</button>
            </div>
          </>
        ) : uploading ? (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Uploading…</div>
        ) : (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 28, marginBottom: 4 }}>📷</div>
            <div style={{ fontSize: 11 }}>Tap to upload</div>
          </div>
        )}
      </div>

      {/* Label + notes */}
      <div style={{ padding: '8px 10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          {done && <span style={{ color: '#27ae60', fontSize: 13 }}>✓</span>}
          <span style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>{photo.label}</span>
        </div>
        <input
          type="text"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          onBlur={handleNotesBlur}
          placeholder="Notes…"
          style={{
            width: '100%', fontSize: 11, padding: '4px 6px',
            border: '1px solid var(--border)', borderRadius: 4,
            background: 'transparent', color: 'var(--text)',
          }}
        />
      </div>

      {/* Hidden file input */}
      <input ref={inputRef} type="file" accept="image/*" capture="environment"
        onChange={handleUpload} style={{ display: 'none' }} />
    </div>
  );
}

// ── Main PhotosSection ────────────────────────────────────────────────────────

export default function PhotosSection({ deal }) {
  const [photos,  setPhotos]  = useState([]);
  const [loading, setLoading] = useState(true);

  const [driveLink, setDriveLink] = useState(null);

  const load = useCallback(() => {
    api.getPhotos(deal.id)
      .then(data => {
        // API now returns { photos, driveFolderLink }
        if (data.photos) {
          setPhotos(data.photos);
          setDriveLink(data.driveFolderLink || null);
        } else {
          // Fallback for old response shape
          setPhotos(Array.isArray(data) ? data : []);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [deal.id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading photo checklist…</div>
  );

  // Group by category display name
  const groups = {};
  for (const photo of photos) {
    const groupKey = getCategoryLabel(photo.category);
    if (!groups[groupKey]) groups[groupKey] = { color: getCategoryColor(photo.category), icon: getCategoryIcon(photo.category), items: [] };
    groups[groupKey].items.push(photo);
  }

  const total = photos.length;
  const done  = photos.filter(p => p.url).length;

  return (
    <div>
      {/* Progress header */}
      <div style={{
        background: 'var(--brand)', color: '#fff', borderRadius: 'var(--radius)',
        padding: '14px 20px', marginBottom: 20,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 2 }}>Property Documentation</div>
          <div style={{ fontWeight: 700, fontSize: 18 }}>
            {done} / {total} photos
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 4 }}>Progress</div>
          <div style={{ width: 120, height: 8, background: 'rgba(255,255,255,0.3)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: `${total ? (done / total) * 100 : 0}%`, height: '100%', background: '#fff', borderRadius: 4, transition: 'width 0.3s' }} />
          </div>
          <div style={{ fontSize: 11, marginTop: 4, opacity: 0.8 }}>
            {total > 0 ? Math.round((done / total) * 100) : 0}% complete
          </div>
        </div>
      </div>

      {/* Drive folder link */}
      {driveLink && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          background: 'var(--bg)', borderRadius: 'var(--radius)',
          padding: '10px 16px', marginBottom: 16,
          border: '1px solid var(--border)',
        }}>
          <span style={{ fontSize: 20 }}>📁</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 1 }}>Google Drive Folder</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>All photos sync here automatically · Anyone with the link can view</div>
          </div>
          <a href={driveLink} target="_blank" rel="noreferrer"
            style={{ background: 'var(--brand)', color: '#fff', padding: '6px 14px',
              borderRadius: 6, fontSize: 12, fontWeight: 600, textDecoration: 'none', flexShrink: 0 }}>
            Open ↗
          </a>
        </div>
      )}

      {/* Groups */}
      {Object.entries(groups).map(([groupName, group]) => {
        const groupDone = group.items.filter(p => p.url).length;
        return (
          <div key={groupName} style={{ marginBottom: 24 }}>
            {/* Group header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              paddingBottom: 6, marginBottom: 12,
              borderBottom: `2px solid ${group.color}`,
            }}>
              <span style={{ fontSize: 18 }}>{group.icon}</span>
              <span style={{ fontWeight: 700, fontSize: 14, color: group.color }}>{groupName}</span>
              <span style={{
                marginLeft: 'auto', fontSize: 11, fontWeight: 600,
                background: groupDone === group.items.length ? '#d4edda' : 'var(--bg)',
                color: groupDone === group.items.length ? '#27ae60' : 'var(--text-muted)',
                padding: '2px 8px', borderRadius: 12,
              }}>
                {groupDone}/{group.items.length}
              </span>
            </div>

            {/* Photo grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: 12,
            }}>
              {group.items.map(photo => (
                <PhotoCard key={photo.id} photo={photo} dealId={deal.id} onRefresh={load} />
              ))}
            </div>
          </div>
        );
      })}

      {photos.length === 0 && (
        <div className="empty-state">
          <div className="icon">📷</div>
          <div>No photo checklist yet. Add rooms and deal info to generate it.</div>
        </div>
      )}

      <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', padding: '8px 0 16px' }}>
        Photos are stored with this deal. Tap any card to upload from camera or file.
      </div>
    </div>
  );
}
